import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { activeNetworkSelector, userSelector } from 'redux/selectors';
import { useLazyGetJobQuery } from 'services/jobsV2';
import { useLazyGetFindemAuthQuery } from 'services/findemV2';
import { useLazyGetSourcingCampaignQuery } from 'services/sourcing';
import toast from 'react-hot-toast';
import strings from 'strings';
import { useLazyGetNetworkQuery } from 'services/organizationsV2';
import { createWidgetConfig, createDynamicFindemIframe, cleanupFindemWidget } from '../../../../helpers/findemWidget';

export const useFindemWidget = () => {
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const widgetInstanceRef = useRef(null);
  const containerRef = useRef(null);
  const activeNetwork = useSelector(activeNetworkSelector);
  const [getJob] = useLazyGetJobQuery();
  const user = useSelector(userSelector);
  const [getFindemAuth] = useLazyGetFindemAuthQuery();
  const [getSourcingCampaign] = useLazyGetSourcingCampaignQuery();
  const [widgetView, setWidgetView] = useState('launch');
  const [getNetwork, { isLoading: isLoadingNetwork, isFetching: isFetchingNetwork }] = useLazyGetNetworkQuery();

  const collectionId = activeNetwork?.id;

  const initializeWidget = useCallback(
    async ({ token, job: jobData, view = 'calibration', ...rest }) => {
      setError(null);

      try {
        // Create widget configuration
        const config = createWidgetConfig({
          job: jobData,
          user,
          token,
          collectionId,
          view,
          ...rest,
        });

        // Load the Findem widget in iframe
        await createDynamicFindemIframe({
          ...config,
        });
        setIsLoading(false);
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    },
    [user, collectionId],
  );

  const clean = useCallback(() => {
    // Destroy widget instance if it exists
    if (widgetInstanceRef.current) {
      if (typeof widgetInstanceRef.current.destroy === 'function') {
        widgetInstanceRef.current.destroy();
      }
      widgetInstanceRef.current = null;
    }

    // Clear the container
    if (containerRef?.current) {
      containerRef.current = null;
    }

    // Clean up global script and references
    cleanupFindemWidget();

    setWidgetView('launch');
    setJob(null);
    // Reset state
    setIsLoading(false);
    setError(null);
  }, []);

  const openWidget = useCallback(
    async ({ view = 'boost', job: jobData }) => {
      try {
        setWidgetView(view);
        setJob(jobData);

        if (view === 'calibration-selection') {
          return;
        }

        setIsLoading(true);

        // get job
        const jobResponse = await getJob({ jobId: jobData.id }).unwrap();
        const mergedJob = { ...jobResponse, ...jobData };

        // get network
        const network = await getNetwork(collectionId).unwrap();

        // Fetch sourcing campaign if it exists
        let sourcingCampaign = null;
        if (mergedJob?.sourcingCampaign?.id) {
          try {
            sourcingCampaign = await getSourcingCampaign({ id: mergedJob.sourcingCampaign.id }).unwrap();
          } catch (fetchError) {
            // If sourcing campaign fetch fails, continue without it
            // eslint-disable-next-line no-console
            console.warn('Failed to fetch sourcing campaign:', fetchError);
          }
        }

        // Extract session IDs from sourcing_campaign_sessions if available
        // Check both camelCase and snake_case property names
        const sessions =
          sourcingCampaign?.sourcingCampaignSessions || sourcingCampaign?.sourcing_campaign_sessions || null;
        if (sessions?.[0]) {
          const session = sessions[0];
          mergedJob.sourcingCampaign = {
            ...mergedJob.sourcingCampaign,
            ...sourcingCampaign,
            calibrateChatSessionId: session.calibrateChatSessionId || session.calibrate_chat_session_id,
            outreachChatSessionId: session.outreachChatSessionId || session.outreach_chat_session_id,
          };
        } else if (sourcingCampaign) {
          // Merge sourcing campaign data even if no sessions
          mergedJob.sourcingCampaign = {
            ...mergedJob.sourcingCampaign,
            ...sourcingCampaign,
          };
        }

        // get the token
        const { token } = await getFindemAuth(collectionId).unwrap();

        await initializeWidget({
          token,
          user,
          job: mergedJob,
          view,
          vcInfo: network?.description,
          vcUrl: network?.domain,
          jobBoardUrl: network?.url ?? '',
        });
      } catch (errorResponse) {
        setIsLoading(false);
        toast.error(errorResponse?.data?.error ?? strings.genericError);
      }
    },
    [getFindemAuth, getJob, getSourcingCampaign, initializeWidget, collectionId, user, getNetwork],
  );

  return {
    isLoading,
    error,
    initializeWidget,
    clean,
    closeWidget: clean,
    openWidget,
    findemError: error,
    isLoadingNetwork: isLoadingNetwork || isFetchingNetwork,
    job,
    widgetView,
    setWidgetView: ({ view, job: jobData }) => {
      setWidgetView(view);
      setJob(jobData);
    },
  };
};
